import React, { useEffect, useState } from 'react';

import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { triggersApi } from '../../api/triggersApi';
import { useRuleStore } from '../../state/ruleStore';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

import type { TriggerDefinitionDTO } from '../../types/dto';
import type { RuleDraft } from '../../types/rule';

type Props = {
  value: RuleDraft;
  onChange: (p: Partial<RuleDraft>) => void;
  plugin: any; // for future: to render plugin config inputs
};

export const GeneralStep: React.FC<Props> = ({ value, onChange }) => {
  const theme = useThemeContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const { selectedTrigger, setSelectedTrigger } = useRuleStore();
  const [triggerDefinitions, setTriggerDefinitions] = useState<TriggerDefinitionDTO[]>([]);
  const [loadingTriggers, setLoadingTriggers] = useState(false);

  // Carregar trigger definitions
  useEffect(() => {
    const loadTriggers = async () => {
      try {
        setLoadingTriggers(true);
        const triggers = await triggersApi.getTriggerDefinitions();
        setTriggerDefinitions(triggers);
      } catch (error) {
        console.error('Erro ao carregar triggers:', error);
      } finally {
        setLoadingTriggers(false);
      }
    };

    loadTriggers();
  }, []);

  const handleTriggerChange = (triggerUuid: string) => {
    console.log('🔍 === handleTriggerChange ===');
    console.log('🔍 triggerUuid:', triggerUuid);

    const trigger = triggerDefinitions.find(t => t.uuid === triggerUuid);
    console.log('🔍 trigger encontrado:', trigger);

    setSelectedTrigger(trigger || null);
    console.log('✅ Trigger selecionado salvo no store');

    // Atualizar eventType baseado no trigger selecionado
    // (por enquanto mantemos form_submission como padrão)
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 4,
      }}
    >
      {/* Coluna esquerda: campos básicos */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{ paddingBottom: 2, fontWeight: 600, fontSize: isMobile ? '1.1rem' : '1.25rem' }}
        >
          Configurações Básicas
        </Typography>
        <TextField
          label="Nome da Regra"
          variant="outlined"
          fullWidth
          value={value.name}
          onChange={e => {
            console.log('🔍 Nome alterado:', e.target.value);
            onChange({ name: e.target.value });
          }}
          sx={{ mb: 2 }}
          size={isMobile ? 'small' : 'medium'}
        />
        <TextField
          label="Descrição"
          variant="outlined"
          fullWidth
          value={value.description ?? ''}
          onChange={e => {
            console.log('🔍 Descrição alterada:', e.target.value);
            onChange({ description: e.target.value });
          }}
          sx={{ mb: 2 }}
          size={isMobile ? 'small' : 'medium'}
          multiline
          rows={isMobile ? 2 : 3}
        />

        {/* Select de Gatilho */}
        <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? 'small' : 'medium'}>
          <InputLabel id="trigger-select-label">Gatilho</InputLabel>
          <Select
            labelId="trigger-select-label"
            id="trigger-select"
            value={selectedTrigger?.uuid || ''}
            label="Gatilho"
            onChange={e => handleTriggerChange(e.target.value)}
            disabled={loadingTriggers}
            sx={{
              '& .MuiSelect-select': {
                color: theme.text.primary,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.text.primary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.text.primary,
              },
            }}
          >
            {loadingTriggers ? (
              <MenuItem disabled>Carregando gatilhos...</MenuItem>
            ) : (
              triggerDefinitions.map(trigger => (
                <MenuItem
                  key={trigger.uuid}
                  value={trigger.uuid}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant="body2">{trigger.name}</Typography>
                  <Typography variant="caption" sx={{ color: theme.text.secondary, mt: 0.5 }}>
                    {trigger.description}
                  </Typography>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Coluna direita: status */}
      <Box
        sx={{
          flex: isMobile ? 'none' : 0.7,
          borderLeft: isMobile ? 'none' : `1px solid ${theme.background.primary}`,
          borderTop: isMobile ? `1px solid ${theme.background.primary}` : 'none',
          pl: isMobile ? 0 : 4,
          pt: isMobile ? 2 : 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{ paddingBottom: 2, fontWeight: 600, fontSize: isMobile ? '1.1rem' : '1.25rem' }}
        >
          Configurações Avançadas
        </Typography>
        <Typography variant="body1" sx={{ mb: 0, fontSize: isMobile ? '0.875rem' : '1rem' }}>
          Selecionar status
        </Typography>
        <RadioGroup
          value={value.isActive ? 'Ativo' : 'Inativo'}
          onChange={e => {
            console.log('🔍 Status alterado:', e.target.value);
            onChange({ isActive: e.target.value === 'Ativo' });
          }}
        >
          <FormControlLabel
            value="Ativo"
            control={
              <Radio
                sx={{
                  color: theme.text.secondary,
                  '&.Mui-checked': { color: theme.primary },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: value.isActive === true ? theme.text.primary : theme.text.secondary,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                }}
              >
                Ativo
              </Typography>
            }
          />
          <FormControlLabel
            value="Inativo"
            control={
              <Radio
                sx={{
                  color: theme.text.secondary,
                  '&.Mui-checked': { color: theme.primary },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: value.isActive === false ? theme.text.primary : theme.text.secondary,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                }}
              >
                Inativo
              </Typography>
            }
          />
        </RadioGroup>
      </Box>
    </Box>
  );
};
